import {
  FacilityIdAppointmentIdParams,
  FacilityIdParam,
  PatientAppointmentFileUrlParams,
  PatientAppointmentUrlParams,
  PatientAppointmentsUrlParams,
  endpointKeys,
  CushlaGetQueryOptions,
} from '@common/api'
import {
  Appointment,
  AppointmentSummary,
  PagedResponse,
  PreAppointmentQuestionnaireResponse,
  ExternalAppointmentScreeningTarget,
  Questionnaire,
  QuestionAnswer,
  PractitionerCreateAppointmentRequest,
  PractitionerCreateAppointmentResponse,
  RowGroup,
  ScreeningConsultation,
  SetAppointmentStatusRequest,
  StaticFile,
  UpdateConsultation,
} from '@common/models'
import {
  appointmentNormaliser,
  appointmentSummaryOrNullNormaliser,
  getPagedResponseNormaliser,
  preAppointmentQuestionnaireResponseNormaliser,
  externalAppointmentScreeningTargetNormaliser,
  questionnaireNormaliser,
  screeningConsultationNormaliser,
  staticFileNormaliser,
  staticFileOrNullNormaliser,
} from '@common/normalisers'

import {useApi, useWebGet, useWebSet, useWebSetFormData} from './helpers'

export type UpdateScreeningConsultationWithFilesPayload = {
  rowGroups: RowGroup[]
  files: File[]
}

function buildScreeningConsultationFormData(
  payload: UpdateScreeningConsultationWithFilesPayload,
): FormData {
  const formData = new FormData()
  formData.append(
    'request',
    new Blob([JSON.stringify({rowGroups: payload.rowGroups})], {
      type: 'application/json',
    }),
  )
  payload.files.forEach((file) => formData.append('files', file))
  return formData
}

export const useGetPatientAppointments = (
  params: PatientAppointmentsUrlParams,
) =>
  useWebGet<PagedResponse<Appointment>>(
    [endpointKeys.patientAppointments, params],
    useApi().patientAppointments(params),
    getPagedResponseNormaliser(appointmentNormaliser),
  )

export const useGetPatientAppointment = (params: PatientAppointmentUrlParams) =>
  useWebGet<Appointment>(
    [endpointKeys.patientAppointment, params],
    useApi().patientAppointment(params),
    appointmentNormaliser,
  )

export const useGetPatientAppointmentFile = (
  params: PatientAppointmentFileUrlParams,
  options: CushlaGetQueryOptions<StaticFile>,
) =>
  useWebGet<StaticFile>(
    [endpointKeys.patientAppointmentFile, params],
    useApi().patientAppointmentFile(params),
    staticFileNormaliser,
    options,
  )

export const useGetPatientAppointmentSummary = (
  params: PatientAppointmentUrlParams,
) =>
  useWebGet<AppointmentSummary | null>(
    [endpointKeys.patientAppointmentSummary, params],
    useApi().patientAppointmentSummary(params),
    appointmentSummaryOrNullNormaliser,
  )

export const useGetPatientAppointmentSummaryFile = (
  params: PatientAppointmentUrlParams,
  options?: CushlaGetQueryOptions<StaticFile | null>,
) =>
  useWebGet<StaticFile | null>(
    [endpointKeys.patientAppointmentSummaryFile, params],
    useApi().patientAppointmentSummaryFile(params),
    staticFileOrNullNormaliser,
    options,
  )

export const useGetScreeningConsultation = (
  params: FacilityIdAppointmentIdParams,
) =>
  useWebGet<ScreeningConsultation>(
    [endpointKeys.screeningConsultation, params],
    useApi().screeningConsultation(params),
    screeningConsultationNormaliser,
  )

export const useUpdateScreeningConsultation = (
  params: FacilityIdAppointmentIdParams,
) =>
  useWebSet<UpdateConsultation>(
    [endpointKeys.screeningConsultation, params],
    useApi().screeningConsultation(params),
    'PATCH',
    undefined,
    [[endpointKeys.screeningConsultation, params], [endpointKeys.appointments]],
  )

export const useUpdateScreeningConsultationWithFiles = (
  params: FacilityIdAppointmentIdParams,
) =>
  useWebSetFormData<UpdateScreeningConsultationWithFilesPayload>(
    [endpointKeys.screeningConsultation, params],
    useApi().screeningConsultation(params),
    'PATCH',
    undefined,
    buildScreeningConsultationFormData,
    [[endpointKeys.screeningConsultation, params], [endpointKeys.appointments]],
  )

export const useCreatePatientAppointment = (params: FacilityIdParam) =>
  useWebSet<
    PractitionerCreateAppointmentRequest,
    PractitionerCreateAppointmentResponse
  >(
    [endpointKeys.appointments],
    useApi().createPatientAppointment(params),
    'POST',
    (data) => data,
    [[endpointKeys.appointments]],
  )

export const useSetAppointmentStatus = (
  params: FacilityIdAppointmentIdParams,
) =>
  useWebSet<SetAppointmentStatusRequest, Appointment>(
    [endpointKeys.practitionerAppointmentStatus, params],
    useApi().practitionerAppointmentStatus(params),
    'PATCH',
    (data) => data,
    [[endpointKeys.appointment, params], [endpointKeys.appointments]],
  )

export const useGetPreAppointmentQuestionnaire = (
  params: FacilityIdAppointmentIdParams,
  options?: CushlaGetQueryOptions<PreAppointmentQuestionnaireResponse>,
) =>
  useWebGet<PreAppointmentQuestionnaireResponse>(
    [endpointKeys.practitionerPreAppointmentQuestionnaire, params],
    useApi().practitionerPreAppointmentQuestionnaire(params),
    preAppointmentQuestionnaireResponseNormaliser,
    {
      retry: false,
      ...options,
    },
  )

export const useGetPreAppointmentQuestionnaireDefinition = (
  params: FacilityIdAppointmentIdParams,
  options?: CushlaGetQueryOptions<Questionnaire>,
) =>
  useWebGet<Questionnaire>(
    [endpointKeys.practitionerPreAppointmentQuestionnaireDefinition, params],
    useApi().practitionerPreAppointmentQuestionnaireDefinition(params),
    questionnaireNormaliser,
    {
      retry: false,
      ...options,
    },
  )

export const useSavePreAppointmentQuestionnaire = (
  params: FacilityIdAppointmentIdParams,
) =>
  useWebSet<{answers: QuestionAnswer[]}, PreAppointmentQuestionnaireResponse>(
    [endpointKeys.practitionerPreAppointmentQuestionnaire, params],
    useApi().practitionerPreAppointmentQuestionnaire(params),
    'PUT',
    preAppointmentQuestionnaireResponseNormaliser,
    [
      [endpointKeys.practitionerPreAppointmentQuestionnaire, params],
      [endpointKeys.practitionerPreAppointmentQuestionnaireDefinition, params],
    ],
  )

export const useResolveExternalAppointment = (
  externalAppointmentId: string | undefined,
) =>
  useWebGet<ExternalAppointmentScreeningTarget>(
    [endpointKeys.externalAppointmentScreeningTarget, externalAppointmentId],
    useApi().externalAppointmentScreeningTarget({
      externalAppointmentId: externalAppointmentId ?? '',
    }),
    externalAppointmentScreeningTargetNormaliser,
    {
      enabled: Boolean(externalAppointmentId),
      retry: false,
    },
  )
